import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Check, Sparkles, Gift, Calendar, Send, Upload, X, Image } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { trackLead, trackViewContent } from "../utils/metaTracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1721273619586-2df84fb12d7f?w=1600&q=80",
  strawberries: "https://images.unsplash.com/photo-1653017186774-c0c19072d534?w=800&q=80",
  cakePops: "https://images.unsplash.com/photo-1586195830864-e4d9688815c8?w=800&q=80",
  macarons: "https://images.unsplash.com/photo-1542691246-11285f1af5a2?w=800&q=80",
  riceKrispies: "https://images.unsplash.com/photo-1590080875515-8a3a8dc5735e?w=800&q=80",
  oreos: "https://images.unsplash.com/photo-1646619271415-f9d1aac4e87a?w=800&q=80",
  pretzels: "https://images.unsplash.com/photo-1515192760710-e68043d6e294?w=800&q=80",
  madeleines: "https://images.unsplash.com/photo-1620292760736-5ccaed8c6fee?w=800&q=80",
  wafers: "https://images.unsplash.com/photo-1772986510963-386fc0181de6?w=800&q=80",
  gallery1: "https://images.unsplash.com/photo-1646902823849-1aa943a17f46?w=600&q=80",
  gallery2: "https://images.unsplash.com/photo-1609281522871-ef95d5ff2cce?w=600&q=80",
  gallery3: "https://images.unsplash.com/photo-1721273650582-4ac26cef7732?w=600&q=80",
  gallery4: "https://images.unsplash.com/photo-1677676700414-ff5d6302a978?w=600&q=80",
  gallery5: "https://images.unsplash.com/photo-1605322154949-0e481904612b?w=600&q=80",
  gallery6: "https://images.unsplash.com/photo-1617694085064-155b7a155f28?w=600&q=80",
};

const TREAT_OPTIONS = [
  "Strawberries",
  "Cake Pops",
  "Macarons",
  "Rice Krispies",
  "Oreos",
  "Pretzels",
  "Madeleines",
  "Wafers",
];

const SIGNATURE_TREATS = [
  {
    title: "Strawberries",
    description: "Hand-dipped in premium Belgian chocolate with custom decorations, gold leaf, and designer drizzles.",
    image: IMAGES.strawberries,
  },
  {
    title: "Cake Pops",
    description: "Custom-shaped cake pops in any flavour and theme. Perfect for party favours and dessert tables.",
    image: IMAGES.cakePops,
  },
  {
    title: "Macarons",
    description: "Delicate, flavourful macarons in your event's colour palette. Available in a variety of signature flavours.",
    image: IMAGES.macarons,
  },
  {
    title: "Rice Krispies",
    description: "Gourmet rice krispie treats dipped in chocolate and decorated to match your event's theme and colours.",
    image: IMAGES.riceKrispies,
  },
  {
    title: "Oreos",
    description: "Chocolate-covered Oreos with custom designs, sprinkles, and edible decorations for any occasion.",
    image: IMAGES.oreos,
  },
  {
    title: "Pretzels",
    description: "Chocolate-dipped pretzel rods with drizzles, sprinkles, and custom colour coordination.",
    image: IMAGES.pretzels,
  },
  {
    title: "Madeleines",
    description: "Classic French madeleines dipped in chocolate and finished with elegant decorative touches.",
    image: IMAGES.madeleines,
  },
  {
    title: "Wafers",
    description: "Chocolate-drizzled wafer sticks and bars with premium toppings, perfect for elegant dessert displays.",
    image: IMAGES.wafers,
  },
];

const EVENT_PACKAGES = [
  {
    name: "Sweet Soiree",
    subtitle: "Choice of 1-2 colours with Drizzled / Sprinkled design",
    includes: [
      "Choice of 1-2 colours",
      "Drizzled / Sprinkled design",
    ],
    price: "Starting at $49 Per Dozen",
    popular: false,
  },
  {
    name: "Grand Celebration",
    subtitle: "Choice of up to 3 colours and up to 3 designs",
    includes: [
      "Choice of up to 3 colours",
      "Up to 3 designs",
      "Drizzled, Sprinkled light design",
      "Chocolate Decor",
      "Display edible image setup included",
    ],
    price: "Starting at $59 Per Dozen",
    popular: true,
  },
  {
    name: "Signature Experience",
    subtitle: "Choice of up to 4 colours and up to 4 designs",
    includes: [
      "Choice of up to 4 colours",
      "Up to 4 designs",
      "Drizzled, Sprinkled light design",
      "Chocolate Decor",
      "Display edible image setup included",
      "Full event styling & setup",
      "Custom branded elements",
    ],
    price: "Starting at $69 Per Dozen",
    popular: false,
  },
];

const GALLERY_IMAGES = [
  IMAGES.gallery1, IMAGES.gallery2, IMAGES.gallery3,
  IMAGES.gallery4, IMAGES.gallery5, IMAGES.gallery6,
];

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const TreatsPage = () => {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", event_type: "", event_date: "",
    quantity_dozens: "", requested_treats: [], inspiration: "", delivery_preference: "",
    selected_package: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [cmsImages, setCmsImages] = useState({});
  const [galleryImages, setGalleryImages] = useState([]);
  const fileInputRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    trackViewContent({ content_name: "Treats Page", content_category: "treats" });
    // Load CMS-uploaded treat images
    axios.get(`${API}/public/treats-images`).then(res => setCmsImages(res.data)).catch(() => {});
    // Load CMS gallery images
    axios.get(`${API}/public/treats-gallery`).then(res => setGalleryImages(res.data)).catch(() => {});
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const getTreatImage = (treatTitle) => {
    const cms = cmsImages[treatTitle];
    if (cms?.file_id) return `${API}/public/treats-images/${cms.file_id}`;
    const found = SIGNATURE_TREATS.find(t => t.title === treatTitle);
    return found?.image || IMAGES.hero;
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleTreat = (treat) => {
    setFormData(prev => ({
      ...prev,
      requested_treats: prev.requested_treats.includes(treat)
        ? prev.requested_treats.filter(t => t !== treat)
        : [...prev.requested_treats, treat],
    }));
  };

  const selectPackage = (pkgName) => {
    setFormData(prev => ({ ...prev, selected_package: pkgName }));
    scrollToForm();
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const valid = files.filter(f => {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} too large (max 10MB)`); return false; }
      if (!f.type.startsWith("image/") && f.type !== "application/pdf") { toast.error(`${f.name}: only images and PDFs allowed`); return false; }
      return true;
    });
    setPendingFiles(prev => [...prev, ...valid.map(f => ({ file: f, preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null }))]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx) => {
    setPendingFiles(prev => {
      const updated = [...prev];
      if (updated[idx].preview) URL.revokeObjectURL(updated[idx].preview);
      updated.splice(idx, 1);
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.event_type) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (formData.requested_treats.length === 0) {
      toast.error("Please select at least one treat");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        requested_treats: formData.requested_treats.join(", "),
        quantity: formData.quantity_dozens ? `${formData.quantity_dozens} dozen` : undefined,
        selected_package: formData.selected_package || undefined,
      };
      delete payload.quantity_dozens;
      const res = await axios.post(`${API}/public/treats-inquiry`, payload);
      const inquiryId = res.data.inquiry_id;

      // Upload reference files to the created inquiry
      if (pendingFiles.length > 0 && inquiryId) {
        for (const { file } of pendingFiles) {
          const fd = new FormData();
          fd.append("file", file);
          try {
            await axios.post(`${API}/public/treats-inquiry/${inquiryId}/upload`, fd);
          } catch {
            // Non-blocking — inquiry already saved
          }
        }
      }

      toast.success("Inquiry submitted! We'll be in touch within 24 hours.");
      trackLead({ name: formData.name, email: formData.email, source: "treats_inquiry" });
      setSubmitted(true);
      setPendingFiles([]);
    } catch (err) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-20" data-testid="treats-page">
      {/* 1. Hero */}
      <section className="relative min-h-[85vh] flex items-center" data-testid="treats-hero">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Luxury dessert table" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/50" />
        </div>
        <div className="container-luxury relative z-10 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <span className="inline-block text-[#D4AF37] uppercase tracking-[0.25em] text-xs font-semibold mb-6">
              Mix & Mingle Treats
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-6 leading-[1.1]">
              Handcrafted Luxury{" "}
              <span className="gold-text">Treats</span>{" "}
              for Every Occasion
            </h1>
            <p className="text-[#A1A1AA] text-lg leading-relaxed mb-10 max-w-xl">
              From chocolate covered strawberries to custom dessert tables &mdash;
              we create edible art that elevates your celebrations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={scrollToForm}
                className="btn-primary flex items-center justify-center gap-2"
                data-testid="treats-inquire-btn"
              >
                Inquire Now <ArrowRight className="w-4 h-4" />
              </button>
              <a href="#signature-treats" className="btn-secondary text-center">
                View Our Treats
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. Brand Positioning */}
      <section className="section-padding bg-[#050505]" data-testid="treats-brand">
        <div className="container-luxury">
          <AnimatedSection>
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-semibold mb-4 block">
                  The Art of Sweet Indulgence
                </span>
                <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-6">
                  Not Just Desserts &mdash;{" "}
                  <span className="font-accent text-[#D4AF37]">An Experience</span>
                </h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-6">
                  Every treat is handcrafted with intention. We don't do generic &mdash;
                  we design luxury confections that match your event's aesthetic, colour palette,
                  and theme down to the last gold-leaf detail.
                </p>
                <p className="text-[#A1A1AA] leading-relaxed mb-8">
                  Whether it's an intimate bridal shower or a grand corporate gala,
                  our treats are the finishing touch that guests remember.
                </p>
                <div className="grid grid-cols-3 gap-6">
                  {[
                    { num: "500+", label: "Events Served" },
                    { num: "100%", label: "Custom Design" },
                    { num: "24hr", label: "Response Time" },
                  ].map((stat) => (
                    <div key={stat.label} className="text-center">
                      <div className="text-2xl font-['Playfair_Display'] font-bold text-[#D4AF37]">{stat.num}</div>
                      <div className="text-[#A1A1AA] text-xs uppercase tracking-widest mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="aspect-[4/5] overflow-hidden relative bg-[#0A0A0A]">
                  <img
                    src={getTreatImage("Strawberries")}
                    alt="Luxury chocolate strawberries"
                    className="w-full h-full object-cover"
                    style={{ filter: "brightness(0.65) contrast(1.15) saturate(0.85)" }}
                  />
                  <div className="absolute inset-0 pointer-events-none" style={{
                    background: `
                      radial-gradient(ellipse at center, transparent 35%, rgba(5,5,5,0.6) 100%),
                      linear-gradient(to top, #050505 0%, rgba(5,5,5,0.2) 30%, transparent 60%, rgba(5,5,5,0.35) 100%)
                    `
                  }} />
                </div>
                <div className="absolute -bottom-6 -left-6 w-48 h-48 border border-[#D4AF37] opacity-20 hidden lg:block" />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 3. Signature Treats */}
      <section id="signature-treats" className="section-padding bg-[#0F0F0F]" data-testid="treats-signature">
        <div className="container-luxury">
          <AnimatedSection className="text-center mb-16">
            <span className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-semibold mb-4 block">
              Our Collection
            </span>
            <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-4">
              Signature Treats
            </h2>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              Each creation is made-to-order with premium ingredients and customized to your vision.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SIGNATURE_TREATS.map((treat, i) => (
              <AnimatedSection key={treat.title}>
                <motion.div
                  whileHover={{ y: -6 }}
                  className="luxury-card overflow-hidden group cursor-pointer bg-[#0A0A0A]"
                  data-testid={`treat-card-${i}`}
                >
                  <div className="aspect-square overflow-hidden relative bg-[#0A0A0A]">
                    <img
                      src={getTreatImage(treat.title)}
                      alt={treat.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      style={{ filter: "brightness(0.65) contrast(1.15) saturate(0.85)" }}
                    />
                    <div className="absolute inset-0 pointer-events-none" style={{
                      background: `
                        radial-gradient(ellipse at center, transparent 30%, rgba(10,10,10,0.6) 100%),
                        linear-gradient(to top, #0A0A0A 0%, rgba(10,10,10,0.35) 25%, transparent 55%, rgba(10,10,10,0.3) 100%)
                      `
                    }} />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#F5F5F0] mb-2">
                      {treat.title}
                    </h3>
                    <p className="text-[#A1A1AA] text-sm leading-relaxed">
                      {treat.description}
                    </p>
                  </div>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Custom Orders */}
      <section className="section-padding bg-[#050505]" data-testid="treats-custom">
        <div className="container-luxury">
          <AnimatedSection>
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1">
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative aspect-square overflow-hidden bg-[#0A0A0A]">
                    <img src={getTreatImage("Rice Krispies")} alt="Rice krispie treats" className="w-full h-full object-cover" style={{ filter: "brightness(0.65) contrast(1.15) saturate(0.85)" }} />
                    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, transparent 30%, rgba(5,5,5,0.6) 100%), linear-gradient(to top, #050505 0%, rgba(5,5,5,0.2) 30%, transparent 60%, rgba(5,5,5,0.35) 100%)" }} />
                  </div>
                  <div className="relative aspect-square overflow-hidden bg-[#0A0A0A] mt-8">
                    <img src={getTreatImage("Madeleines")} alt="Madeleines" className="w-full h-full object-cover" style={{ filter: "brightness(0.65) contrast(1.15) saturate(0.85)" }} />
                    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, transparent 30%, rgba(5,5,5,0.6) 100%), linear-gradient(to top, #050505 0%, rgba(5,5,5,0.2) 30%, transparent 60%, rgba(5,5,5,0.35) 100%)" }} />
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <span className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-semibold mb-4 block">
                  Bespoke Creations
                </span>
                <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-6">
                  Custom Orders
                </h2>
                <p className="text-[#A1A1AA] leading-relaxed mb-8">
                  Have a unique vision? We bring it to life. From themed dessert tables to
                  branded corporate treats, every detail is tailored to your specifications.
                </p>
                <div className="space-y-4 mb-8">
                  {[
                    "Custom colours & themes to match your event",
                    "Edible images, logos & monograms available",
                    "Dietary accommodations (gluten-free, vegan options)",
                    "Rush orders available with 48-hour notice",
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-[#A1A1AA] text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={scrollToForm}
                  className="btn-primary flex items-center gap-2"
                  data-testid="treats-custom-order-btn"
                >
                  <Gift className="w-4 h-4" /> Start Your Custom Order
                </button>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 5. Event Packages */}
      <section className="section-padding bg-[#0F0F0F]" data-testid="treats-packages">
        <div className="container-luxury">
          <AnimatedSection className="text-center mb-16">
            <span className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-semibold mb-4 block">
              Curated Collections
            </span>
            <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-4">
              Event Packages
            </h2>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto mb-6">
              Pre-designed packages for easy ordering. Select your treats and package tier below.
            </p>
            <p className="text-[#D4AF37]/80 text-sm italic max-w-2xl mx-auto">
              Please keep in mind that these are starting prices and may vary depending on final details, colors, designs etc...
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            {EVENT_PACKAGES.map((pkg, i) => (
              <AnimatedSection key={pkg.name}>
                <div
                  className={`relative p-8 border transition-all duration-500 hover:-translate-y-2 flex flex-col ${
                    pkg.popular
                      ? "border-[#D4AF37] bg-[#121212]"
                      : "border-[rgba(212,175,55,0.15)] bg-[#121212] hover:border-[rgba(212,175,55,0.4)]"
                  }`}
                  data-testid={`treats-package-${i}`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-[#D4AF37] text-[#050505] px-4 py-1 text-[10px] uppercase tracking-widest font-bold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <div className="text-center mb-4 pt-2">
                    <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] mb-2">
                      {pkg.name}
                    </h3>
                    <p className="text-[#A1A1AA] text-xs leading-relaxed">{pkg.subtitle}</p>
                  </div>
                  <div className="gold-divider mx-auto mb-5" />

                  {/* Choose your treats */}
                  <p className="text-[#52525B] text-[10px] uppercase tracking-widest font-semibold mb-3">
                    Available Treats
                  </p>
                  <div className="grid grid-cols-2 gap-2 mb-5">
                    {TREAT_OPTIONS.map((treat) => (
                      <div key={treat} className="flex items-center gap-2">
                        <Sparkles className="w-3 h-3 text-[#D4AF37] flex-shrink-0" />
                        <span className="text-[#A1A1AA] text-xs">{treat}</span>
                      </div>
                    ))}
                  </div>

                  {/* What's included */}
                  <p className="text-[#52525B] text-[10px] uppercase tracking-widest font-semibold mb-3">
                    Includes
                  </p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {pkg.includes.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <Check className="w-3 h-3 text-[#D4AF37] mt-1 flex-shrink-0" />
                        <span className="text-[#A1A1AA] text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="text-center mt-auto">
                    <p className="text-[#D4AF37] font-semibold text-lg mb-4">{pkg.price}</p>
                    <button
                      onClick={() => selectPackage(pkg.name)}
                      className={`w-full py-3 text-sm uppercase tracking-widest font-semibold transition-all ${
                        pkg.popular
                          ? "bg-[#D4AF37] text-[#050505] hover:bg-[#F7E7CE]"
                          : "border border-[#D4AF37] text-[#D4AF37] hover:bg-[rgba(212,175,55,0.1)]"
                      }`}
                      data-testid={`select-package-${i}`}
                    >
                      Select Package
                    </button>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Gallery */}
      <section className="section-padding bg-[#050505]" data-testid="treats-gallery">
        <div className="container-luxury">
          <AnimatedSection className="text-center mb-16">
            <span className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-semibold mb-4 block">
              Our Work
            </span>
            <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">
              Gallery
            </h2>
          </AnimatedSection>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {(galleryImages.length > 0
              ? galleryImages.map(g => ({ src: `${API}/public/treats-images/${g.file_id}`, key: g.file_id }))
              : GALLERY_IMAGES.map((src, i) => ({ src, key: `stock-${i}` }))
            ).map(({ src, key }, i) => (
              <AnimatedSection key={key}>
                <div className="image-zoom aspect-square relative overflow-hidden bg-[#0A0A0A]" data-testid={`gallery-img-${i}`}>
                  <img
                    src={src}
                    alt={`Treats gallery ${i + 1}`}
                    className="w-full h-full object-cover"
                    style={{ filter: "brightness(0.65) contrast(1.15) saturate(0.85)" }}
                  />
                  <div className="absolute inset-0 pointer-events-none" style={{
                    background: `
                      radial-gradient(ellipse at center, transparent 35%, rgba(5,5,5,0.5) 100%),
                      linear-gradient(to top, rgba(5,5,5,0.4) 0%, transparent 40%, transparent 70%, rgba(5,5,5,0.3) 100%)
                    `
                  }} />
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Inquiry Form */}
      <section ref={formRef} className="section-padding bg-[#0F0F0F]" data-testid="treats-inquiry-form">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto">
            <AnimatedSection className="text-center mb-12">
              <span className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-semibold mb-4 block">
                Get Started
              </span>
              <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-4">
                Place Your Order
              </h2>
              <p className="text-[#A1A1AA]">
                Tell us about your event and we'll create a custom quote within 24 hours.
              </p>
            </AnimatedSection>

            {submitted ? (
              <AnimatedSection>
                <div className="text-center py-16 border border-[rgba(212,175,55,0.3)] bg-[#121212]" data-testid="treats-inquiry-success">
                  <div className="success-checkmark">
                    <Check className="w-10 h-10" />
                  </div>
                  <h3 className="text-2xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] mb-3">
                    Inquiry Received!
                  </h3>
                  <p className="text-[#A1A1AA] max-w-md mx-auto mb-6">
                    Thank you for your interest. We'll review your request and get back to you
                    within 24 hours with a custom quote.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({
                        name: "", email: "", phone: "", event_type: "", event_date: "",
                        quantity_dozens: "", requested_treats: [], inspiration: "", delivery_preference: "",
                        selected_package: "",
                      });
                    }}
                    className="btn-secondary text-sm"
                    data-testid="treats-new-inquiry-btn"
                  >
                    Submit Another Inquiry
                  </button>
                </div>
              </AnimatedSection>
            ) : (
              <AnimatedSection>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="treats-form">
                  {/* Selected Package Indicator */}
                  {formData.selected_package && (
                    <div className="flex items-center justify-between p-4 border border-[#D4AF37] bg-[#D4AF37]/5">
                      <div className="flex items-center gap-3">
                        <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                        <span className="text-[#F5F5F0] text-sm">
                          Selected Package: <span className="text-[#D4AF37] font-semibold">{formData.selected_package}</span>
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, selected_package: "" }))}
                        className="text-[#52525B] hover:text-[#F5F5F0] text-xs uppercase tracking-wider"
                      >
                        Clear
                      </button>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text" name="name" value={formData.name} onChange={handleChange}
                        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-4 focus:border-[#D4AF37] focus:outline-none"
                        required data-testid="treats-name"
                      />
                    </div>
                    <div>
                      <label className="form-label">Email *</label>
                      <input
                        type="email" name="email" value={formData.email} onChange={handleChange}
                        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-4 focus:border-[#D4AF37] focus:outline-none"
                        required data-testid="treats-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Phone</label>
                      <input
                        type="tel" name="phone" value={formData.phone} onChange={handleChange}
                        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-4 focus:border-[#D4AF37] focus:outline-none"
                        data-testid="treats-phone"
                      />
                    </div>
                    <div>
                      <label className="form-label">Event Type *</label>
                      <select
                        name="event_type" value={formData.event_type} onChange={handleChange}
                        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-4 focus:border-[#D4AF37] focus:outline-none"
                        required data-testid="treats-event-type"
                      >
                        <option value="">Select event type</option>
                        <option value="Wedding">Wedding</option>
                        <option value="Baby Shower">Baby Shower</option>
                        <option value="Birthday Party">Birthday Party</option>
                        <option value="Corporate Event">Corporate Event</option>
                        <option value="Bridal Shower">Bridal Shower</option>
                        <option value="Anniversary">Anniversary</option>
                        <option value="Holiday Party">Holiday Party</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Event Date</label>
                      <input
                        type="date" name="event_date" value={formData.event_date} onChange={handleChange}
                        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-4 focus:border-[#D4AF37] focus:outline-none"
                        data-testid="treats-event-date"
                      />
                    </div>
                    <div>
                      <label className="form-label">Quantity (Dozens) *</label>
                      <select
                        name="quantity_dozens" value={formData.quantity_dozens} onChange={handleChange}
                        className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-4 focus:border-[#D4AF37] focus:outline-none"
                        required data-testid="treats-quantity-dozens"
                      >
                        <option value="">Select quantity</option>
                        <option value="1">1 Dozen (12 pieces)</option>
                        <option value="2">2 Dozen (24 pieces)</option>
                        <option value="3">3 Dozen (36 pieces)</option>
                        <option value="4">4 Dozen (48 pieces)</option>
                        <option value="5">5 Dozen (60 pieces)</option>
                        <option value="6">6 Dozen (72 pieces)</option>
                        <option value="8">8 Dozen (96 pieces)</option>
                        <option value="10">10 Dozen (120 pieces)</option>
                        <option value="10+">10+ Dozen (Contact for quote)</option>
                      </select>
                    </div>
                  </div>

                  {/* Treat Selection Checkboxes */}
                  <div>
                    <label className="form-label">Select Your Treats *</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-2">
                      {TREAT_OPTIONS.map((treat) => {
                        const isSelected = formData.requested_treats.includes(treat);
                        return (
                          <button
                            key={treat}
                            type="button"
                            onClick={() => toggleTreat(treat)}
                            className={`p-3 text-sm text-left border transition-all flex items-center gap-2 ${
                              isSelected
                                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                                : "border-[rgba(212,175,55,0.15)] text-[#A1A1AA] hover:border-[rgba(212,175,55,0.4)] hover:text-[#F5F5F0]"
                            }`}
                            data-testid={`treat-option-${treat.toLowerCase().replace(/\s/g, "-")}`}
                          >
                            <div className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 ${
                              isSelected ? "border-[#D4AF37] bg-[#D4AF37]" : "border-[#52525B]"
                            }`}>
                              {isSelected && <Check className="w-3 h-3 text-[#050505]" />}
                            </div>
                            {treat}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Inspiration / Details</label>
                    <textarea
                      name="inspiration" value={formData.inspiration} onChange={handleChange} rows={4}
                      placeholder="Tell us about your theme, colours, or any design ideas..."
                      className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-4 focus:border-[#D4AF37] focus:outline-none resize-none placeholder:text-[#52525B]"
                      data-testid="treats-inspiration"
                    />
                  </div>

                  {/* Reference Image Upload */}
                  <div>
                    <label className="form-label">Please upload any reference pictures below</label>
                    <div
                      className="mt-2 border-2 border-dashed border-[rgba(212,175,55,0.3)] p-6 text-center cursor-pointer hover:border-[#D4AF37] transition-colors bg-[#121212]"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="treats-file-upload"
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,.pdf"
                        multiple
                        className="hidden"
                        onChange={handleFileSelect}
                      />
                      <Upload className="w-8 h-8 text-[#D4AF37]/50 mx-auto mb-2" />
                      <p className="text-[#A1A1AA] text-sm">
                        Click to upload or drag & drop
                      </p>
                      <p className="text-[#52525B] text-xs mt-1">
                        Images & PDFs up to 10MB
                      </p>
                    </div>

                    {pendingFiles.length > 0 && (
                      <div className="mt-3 space-y-2" data-testid="treats-uploaded-files">
                        {pendingFiles.map((f, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-2 border border-[rgba(212,175,55,0.15)] bg-[#121212]">
                            {f.preview ? (
                              <img src={f.preview} alt="" className="w-12 h-12 object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                                <Image className="w-5 h-5 text-[#D4AF37]" />
                              </div>
                            )}
                            <span className="text-[#A1A1AA] text-sm flex-1 truncate">{f.file.name}</span>
                            <button
                              type="button"
                              onClick={() => removeFile(idx)}
                              className="p-1 text-[#52525B] hover:text-red-400 flex-shrink-0"
                              data-testid={`remove-file-${idx}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="form-label">Pickup or Delivery *</label>
                    <div className="flex gap-6 mt-2">
                      {["Pickup", "Delivery", "Not Sure"].map((opt) => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer group">
                          <input
                            type="radio" name="delivery_preference" value={opt}
                            checked={formData.delivery_preference === opt}
                            onChange={handleChange}
                            className="accent-[#D4AF37] w-4 h-4"
                            data-testid={`treats-delivery-${opt.toLowerCase().replace(/\s/g, "-")}`}
                          />
                          <span className="text-[#A1A1AA] text-sm group-hover:text-[#F5F5F0] transition-colors">
                            {opt}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <button
                    type="submit" disabled={submitting}
                    className="btn-primary w-full flex items-center justify-center gap-2 mt-4"
                    data-testid="treats-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" /> Submit Inquiry
                      </>
                    )}
                  </button>
                </form>
              </AnimatedSection>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default TreatsPage;
